import { Component, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	BadgeComponent,
	CardSelectComponent,
	DividerComponent,
	FormGroupComponent,
	IconComponent,
	InfoGroupComponent,
	InputComponent,
	InputQuantityComponent,
	ItemComponent,
	ItemGroupComponent,
	ISelect,
	IToogleSelect,
	LinkComponent,
	ListDetailComponent,
	SelectComponent,
	SwitchComponent,
	TagComponent,
	TextareaComponent,
	TileComponent,
	ToggleComponent,
	TooltipComponent,
} from '@tecalisdev/infinity';
import { environment } from '../../../../../environments/environment';
import { DEMAND_LEVEL, OCR_DOC_COUNTRY_TYPE } from '../../../models/workflows.models';
import { OPERATION_TYPE } from '../../../../operations/models/operations.models';
import {
	isOcrActiveCaptureDefault,
	OCR_OBFUSCATION_FIELD_KEYS,
	ocrObfuscationFieldI18nKey,
} from '../../../utils/workflow-ocr.util';
import { WorkflowFormOperationPanelBase } from './workflow-form-operation-panel.base';
import { WorkflowFormAdvancedCollapseComponent } from '../workflow-form-advanced-collapse.component';

/**
 * Panel UI de OCR en el editor (NIF-530 / NIF-845).
 * Abrir cuando: docs/países, modo/fallback, fraud scoring, MiDNI o EUDI Wallet.
 */
@Component({
	selector: 'app-workflow-form-ocr-panel',
	imports: [
		WorkflowFormAdvancedCollapseComponent,
		NgTemplateOutlet,
		ReactiveFormsModule,
		TranslatePipe,
		TileComponent,
		ItemComponent,
		ItemGroupComponent,
		FormGroupComponent,
		CardSelectComponent,
		ToggleComponent,
		SelectComponent,
		InputComponent,
		InputQuantityComponent,
		ListDetailComponent,
		SwitchComponent,
		InfoGroupComponent,
		TooltipComponent,
		LinkComponent,
		DividerComponent,
		IconComponent,
		BadgeComponent,
		TagComponent,
		TextareaComponent,
	],
	templateUrl: './workflow-form-ocr-panel.component.html',
})
export class WorkflowFormOcrPanelComponent extends WorkflowFormOperationPanelBase {
	protected readonly operationType = OPERATION_TYPE.OCR;

	private readonly translate = inject(TranslateService);

	public readonly OCR_DOC_COUNTRY_TYPE = OCR_DOC_COUNTRY_TYPE;
	public readonly DEMAND_LEVEL = DEMAND_LEVEL;

	/** Opciones país DNI/Passport compartidas (NIF-845). */
	public readonly ocrDocCountryTypeOptions: ISelect[] = [
		{
			id: OCR_DOC_COUNTRY_TYPE.ALL,
			label: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.ALLOWED_COUNTRIES_OPTIONS.ALL'),
		},
		{
			id: OCR_DOC_COUNTRY_TYPE.INCLUDE,
			label: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.ALLOWED_COUNTRIES_OPTIONS.ONLY_SELECTED'),
		},
		{
			id: OCR_DOC_COUNTRY_TYPE.EXCLUDE,
			label: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.ALLOWED_COUNTRIES_OPTIONS.EXCLUDE_SELECTED'),
		},
	];

	public get obfuscationFieldOptions(): { id: string; label: string }[] {
		return OCR_OBFUSCATION_FIELD_KEYS.map((id) => ({
			id,
			label: this.translate.instant(ocrObfuscationFieldI18nKey(id)),
		}));
	}

	/** Switch maestro de ofuscación (NIF-536 / diseño Configuración). */
	public get obfuscationEnabled(): boolean {
		return this.readBool('obfuscation.enabled');
	}

	/**
	 * Al apagar el switch se vacían los campos para omitir el bloque en el payload.
	 */
	public onObfuscationEnabledChange(enabled: boolean): void {
		this.patchControl('obfuscation.enabled', enabled);
		if (!enabled) {
			this.patchControl('obfuscation.fields', []);
		}
	}

	/** Options estables: `tec-toggle` OnPush recrea controles si `[options]` cambia de ref (NIF-949). */
	public readonly ocrModeOptions: IToogleSelect[] = [
		{
			id: 'auto',
			label: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.OCR_MODE_ACTIVE'),
			tooltip: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.OCR_MODE_ACTIVE_TOOLTIP'),
		},
		{
			id: 'manual',
			label: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.OCR_MODE_STATIC'),
			tooltip: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.OCR_MODE_STATIC_TOOLTIP'),
		},
		{
			id: 'flexible',
			label: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.OCR_MODE_FLEXIBLE'),
			tooltip: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.OCR_MODE_FLEXIBLE_TOOLTIP'),
		},
	];

	public readonly ocrFallbackOptions: IToogleSelect[] = [
		{
			id: 'auto',
			label: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.FALLBACK_FINISH'),
		},
		{
			id: 'manual',
			label: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.FALLBACK_MANUAL'),
			tooltip: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.FALLBACK_MANUAL_TOOLTIP'),
		},
	];

	public readonly ocrFallbackValidationOptions: IToogleSelect[] = [
		{
			id: 'auto',
			label: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.FALLBACK_VALIDATION_AUTO'),
		},
		{
			id: 'manual',
			label: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.FALLBACK_VALIDATION_MANUAL'),
		},
	];

	public get allCountries() {
		return this.host.allCountries;
	}

	public get company() {
		return this.host.company;
	}

	public get ocrFraudScoring(): FormGroup {
		return this.host.ocrFraudScoring;
	}

	public get mode(): string {
		return this.readSelectId('mode');
	}

	/** auto y flexible: captura activa por defecto → bloque de gestión de fallos. */
	public get isActiveCaptureDefault(): boolean {
		return isOcrActiveCaptureDefault(this.mode);
	}

	public get fallback(): string {
		return this.readSelectId('fallback');
	}

	public get fallbackValidation(): string {
		return this.readSelectId('fallback_validation');
	}

	public get fraudDemandType(): string {
		return this.readSelectId('fraud_scoring.type');
	}

	public get dniAvailable(): boolean {
		return this.readBool('dni_available');
	}

	public get passportAvailable(): boolean {
		return this.readBool('passport_available');
	}

	public get nieAvailable(): boolean {
		return this.readBool('nie_available');
	}

	/** NIF-960: card EUDI solo en INT/local (`environment.eudiWalletAllowed`). */
	public get eudiWalletAllowed(): boolean {
		return environment.eudiWalletAllowed === true;
	}

	/** NIF-960: 4ª card EUDI Wallet. */
	public get eudiWalletEnabled(): boolean {
		return this.eudiWalletAllowed && this.readBool('eudi_wallet_enabled');
	}

	/** POL del workflow activo (lectura cruzada para MiDNI / EUDI). */
	public get polAvailable(): boolean {
		return Boolean(this.form.get(`operations.${OPERATION_TYPE.POL}.available`)?.value);
	}

	/** Figma: bloque Requerir/Omitir POL solo con EUDI + POL. */
	public get showEudiPolBlock(): boolean {
		return this.eudiWalletEnabled && this.polAvailable;
	}

	/** Figma: Requerir paso | Omitir paso (omitir = true, default). */
	public readonly eudiPolOptions: IToogleSelect[] = [
		{
			id: 'false',
			label: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.EUDI_POL_REQUIRE'),
		},
		{
			id: 'true',
			label: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.EUDI_POL_OMIT'),
		},
	];

	public get showMidniSkipPol(): boolean {
		return this.readBool('midni_validation_enabled') && this.polAvailable;
	}

	/** Margen de gracia solo tiene sentido si «Documento en vigor» está activo (NIF-862). */
	public get showAllowedDaysExpired(): boolean {
		return this.readBool('fraud_scoring.expirated');
	}

	public isSpainAllowedForDni(): boolean {
		return this.host.isSpainAllowedForDni();
	}

	public isCompanyCountrySpain(): boolean {
		return this.host.isCompanyCountrySpain();
	}

	public getCompanyCountryName(): string {
		return this.host.getCompanyCountryName();
	}

	public isDocCountryFiltered(controlPath: 'dni_type' | 'passport_type'): boolean {
		const id = this.readSelectId(controlPath);
		const raw = this.group.get(controlPath)?.value;
		const value = typeof raw === 'object' && raw && 'id' in raw ? (raw as { id: string }).id : raw;
		return (value ?? id) !== OCR_DOC_COUNTRY_TYPE.ALL;
	}

	public get hasCustomFraudChecks(): boolean {
		return (
			this.readBool('fraud_scoring.manipulated_photo.available') ||
			this.readBool('fraud_scoring.photocopy_check.available') ||
			this.readBool('fraud_scoring.screen_photo.available')
		);
	}

	public setDocAvailable(doc: 'dni_available' | 'passport_available' | 'nie_available', selected: boolean): void {
		this.patchControl(doc, selected);
	}

	public setEudiWalletEnabled(selected: boolean): void {
		if (!this.eudiWalletAllowed) {
			this.patchControl('eudi_wallet_enabled', false);
			return;
		}
		this.patchControl('eudi_wallet_enabled', selected);
	}

	public openOcrFraudModal(): void {
		this.host.showModalOcrFraudScoring = { show: true, form: this.host.ocrFraudScoring };
	}
}
